import firebase from "firebase/compat/app";
import "firebase/compat/database";
import ICharity from "../types/services/ICharity";

const BATCH_SIZE = 15;
const EXCLUDED_CHARITY_IDS = new Set(["superadmin", "donation-reader", ""]);

export type CharityMetadataPatch = {
  id: string;
  name?: string;
  disableGive?: boolean;
};

type PrefetchListener = (inProgress: boolean) => void;

let prefetchInProgress = false;
let prefetchGeneration = 0;
let inFlightPrefetch: Promise<void> | null = null;
let inFlightPrefetchKey = "";
const prefetchListeners = new Set<PrefetchListener>();

export function subscribeCharityNamesPrefetch(
  listener: PrefetchListener
): () => void {
  prefetchListeners.add(listener);
  listener(prefetchInProgress);
  return () => prefetchListeners.delete(listener);
}

export function isCharityNamesPrefetchInProgress(): boolean {
  return prefetchInProgress;
}

function notifyPrefetchListeners(inProgress: boolean) {
  prefetchInProgress = inProgress;
  prefetchListeners.forEach(listener => listener(inProgress));
}

export function resolveCharityDisplayName(
  info: Record<string, unknown> | null
): string | undefined {
  if (!info) {
    return undefined;
  }
  const adminName = info.adminName;
  if (typeof adminName === "string" && adminName.length > 0) {
    return adminName;
  }
  const name = info.name;
  if (typeof name === "string" && name.length > 0) {
    return name;
  }
  return undefined;
}

async function fetchCharityMetadataOnce(
  charityId: string
): Promise<CharityMetadataPatch> {
  const db = firebase.database();
  const [nameSnap, adminNameSnap, disableGiveSnap] = await Promise.all([
    db.ref(`${charityId}/info/name`).once("value"),
    db.ref(`${charityId}/info/adminName`).once("value"),
    db.ref(`${charityId}/config/disableGive`).once("value"),
  ]);
  const patch: CharityMetadataPatch = { id: charityId };
  const name = resolveCharityDisplayName({
    name: nameSnap.val(),
    adminName: adminNameSnap.val(),
  });
  if (name) {
    patch.name = name;
  }
  const disableGive = disableGiveSnap.val();
  if (typeof disableGive === "boolean") {
    patch.disableGive = disableGive;
  }
  return patch;
}

export function mergeCharityMetadataPatch(
  charities: ICharity[],
  patch: CharityMetadataPatch
): ICharity[] {
  const index = charities.findIndex(c => c.id === patch.id);
  if (index < 0) {
    return charities;
  }
  const current = charities[index];
  let next = current;
  let changed = false;
  if (patch.name && !current.name) {
    next = { ...next, name: patch.name };
    changed = true;
  }
  if (
    typeof patch.disableGive === "boolean" &&
    current.disableGive !== patch.disableGive
  ) {
    next = { ...next, disableGive: patch.disableGive };
    changed = true;
  }
  if (!changed) {
    return charities;
  }
  const copy = [...charities];
  copy[index] = next;
  return copy;
}

export function mergeCharityMetadataPatches(
  charities: ICharity[],
  patches: CharityMetadataPatch[]
): ICharity[] {
  return patches.reduce(
    (acc, patch) => mergeCharityMetadataPatch(acc, patch),
    charities
  );
}

function charitiesNeedingPrefetch(charities: ICharity[]): ICharity[] {
  return charities.filter(
    c => c.id && !EXCLUDED_CHARITY_IDS.has(c.id) && !c.name
  );
}

function charityListKey(charities: ICharity[]): string {
  return charities.map(c => c.id).join("\0");
}

export function cancelCharityPickerMetadataPrefetch(): void {
  prefetchGeneration += 1;
  inFlightPrefetch = null;
  inFlightPrefetchKey = "";
  notifyPrefetchListeners(false);
}

async function prefetchCharityPickerMetadata(
  charities: ICharity[],
  onBatch: (merged: ICharity[]) => void
): Promise<void> {
  const targets = charitiesNeedingPrefetch(charities);
  if (targets.length === 0) {
    notifyPrefetchListeners(false);
    return;
  }

  const generation = ++prefetchGeneration;
  notifyPrefetchListeners(true);
  let working = [...charities];

  try {
    for (let i = 0; i < targets.length; i += BATCH_SIZE) {
      if (generation !== prefetchGeneration) {
        return;
      }
      const batch = targets.slice(i, i + BATCH_SIZE);
      const patches = await Promise.all(
        batch.map(c => fetchCharityMetadataOnce(c.id))
      );
      if (generation !== prefetchGeneration) {
        return;
      }
      working = mergeCharityMetadataPatches(working, patches);
      onBatch(working);
      await new Promise<void>(resolve => setTimeout(resolve, 16));
    }
  } finally {
    if (generation === prefetchGeneration) {
      notifyPrefetchListeners(false);
    }
  }
}

/** Loads picker names on demand (picker open). Dedupes concurrent calls for the same charity list. */
export function ensureCharityPickerMetadata(
  charities: ICharity[],
  onBatch: (merged: ICharity[]) => void
): Promise<void> {
  const key = charityListKey(charities);
  if (charitiesNeedingPrefetch(charities).length === 0) {
    return Promise.resolve();
  }
  if (inFlightPrefetch && inFlightPrefetchKey === key) {
    return inFlightPrefetch;
  }
  inFlightPrefetchKey = key;
  inFlightPrefetch = prefetchCharityPickerMetadata(charities, onBatch).finally(
    () => {
      if (inFlightPrefetchKey === key) {
        inFlightPrefetch = null;
      }
    }
  );
  return inFlightPrefetch;
}

export async function fetchMissingCharityMetadata(
  charities: ICharity[]
): Promise<ICharity[]> {
  const targets = charitiesNeedingPrefetch(charities);
  if (targets.length === 0) {
    return charities;
  }
  const patches = await Promise.all(
    targets.map(c => fetchCharityMetadataOnce(c.id))
  );
  return mergeCharityMetadataPatches(charities, patches);
}

export function countSelectableCharities(charities: ICharity[]): number {
  return charities.filter(c => c.id && !EXCLUDED_CHARITY_IDS.has(c.id)).length;
}
